import {
  OrganizationSearchFieldProps,
  OrganizationSearchFieldState,
  SearchFor,
  SearchResult,
  SearchResultType,
  UseFetchResultsArgs
} from './OrganizationSearchField.types'
import {
  getSearchMembersAndTeamsEndpoint,
  getSearchMembersEndpoint,
  getSearchQueryString,
  getSearchResultsFromSearchData,
  getSearchTeamsEndpoint,
  searchResultsAreEqual
} from './OrganizationSearchField.utils'

import { useAuthState } from '../../../../hooks/useAuthState'
import { useDebounce } from 'use-debounce'
import { useEffect, useMemo, useState } from 'react'
import { useFetcher } from '../../../../components/org/hooks/useOrgHooks'
import isEmail from 'validator/lib/isEmail'
import useSWR from 'swr'

function useSearchEndpoint(
  searchFor: SearchFor,
  searchTerm: string,
  orgId?: string
): string | null {
  const { jwt } = useAuthState()
  const queryString = getSearchQueryString(searchFor, searchTerm)
  const endpoint = useMemo(() => {
    const _orgId = orgId || jwt?.org?.orgId || ''
    switch (searchFor) {
      case 'members':
        return getSearchMembersEndpoint(queryString, _orgId)
      case 'teams':
        return getSearchTeamsEndpoint(queryString, _orgId)
      case 'membersAndTeams':
        return getSearchMembersAndTeamsEndpoint(queryString, _orgId)
      default:
        return ''
    }
  }, [jwt, queryString, searchFor, orgId])

  return endpoint
}

export function useFetchSearchResults(args: UseFetchResultsArgs): SearchResult[] {
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const fetcher = useFetcher()
  const endpoint = useSearchEndpoint(args.searchFor, args.searchTerm, args.orgId)
  const { data, error, isValidating } = useSWR(args.disableFetch ? null : endpoint, fetcher)
  const loading = useMemo(() => !data || isValidating, [data, isValidating])
  const setLoading = args?.setLoading
  useEffect(() => {
    setLoading?.(loading)
  }, [loading, setLoading])

  useEffect(() => {
    if (error || data?.status !== 200) {
      if (!loading) {
        setSearchResults([])
      }
    } else if (data) {
      const searchData = data?.collaborators ?? data?.orgMembers ?? data?.teams ?? []
      setSearchResults(getSearchResultsFromSearchData(searchData))
    }
  }, [loading, data, error])

  return searchResults
}

export function useOrganizationSearchFieldState(
  props: Omit<OrganizationSearchFieldProps, 'onSearchResultClicked'>
): OrganizationSearchFieldState {
  const { showInvitationPrompt, searchFor, removeFromResults } = props
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const [loading, setLoading] = useState(false)
  const fetchedResults = useFetchSearchResults({
    searchTerm: debouncedSearchTerm,
    setLoading,
    searchFor,
    disableFetch: props.disableFetch,
    orgId: props.orgId
  })

  const searchResults = useMemo<SearchResult[]>(() => {
    if (fetchedResults.length === 0 && isEmail(searchTerm) && showInvitationPrompt) {
      return [{ type: SearchResultType.INVITATION_PROMPT, email: searchTerm }]
    }
    if (removeFromResults) {
      return fetchedResults.filter(fetched => {
        const fetchedResultShouldBeRemoved = removeFromResults.some(removed =>
          searchResultsAreEqual(fetched, removed)
        )
        return !fetchedResultShouldBeRemoved
      })
    }
    return fetchedResults
  }, [fetchedResults, searchTerm, showInvitationPrompt, removeFromResults])

  return {
    searchResults,
    searchTerm,
    setSearchTerm,
    loading
  }
}
